/**
 * ofacAutoScan — headless OFAC scan + persistence-patch builders.
 *
 * Extracted from OfacScanSection (Bill 2026-07-23, Peter's-cart post-mortem)
 * so the PdwShell terminal Save can AUTO-FIRE the sanctions screen for NEW
 * persons without the FSR ever visiting the Verify section. OfacScanSection
 * imports these same helpers for its visible "Run" button, so the two paths
 * can never drift on wire shape, classification rollup, or persisted keys.
 *
 * Policy (Bill 2026-07-23 — tri-layer alignment with alpha.354):
 *  - Auto-fire applies to NEW persons only (no cifId). Existing-CIF people
 *    are deliberately NOT screened in the modal (2026-05-21 rule); the server
 *    rescreen backstop covers them silently at commit-validate.
 *  - Skip when a satisfactory result (clear / archived-only / FSR override)
 *    is already recorded within the server staleness window
 *    (SystemSetting `risk.ofac.rescreenStaleAfterDays`, server default 1) —
 *    FE and BE share the one `extra.ofacScannedAt` age signal.
 *  - clear / archived-only → silent save-through. hit-current → the modal
 *    stays open on the normal hit workflow. Scan error → modal stays open
 *    with status 'pending' (the commit gate remains the fail-closed backstop).
 */
import type { ApiClient, OfacClassification, SanctionsScreenResult } from '@retail-ops/api-client';
import type { RaoPerson } from '../../../types';
import { normalizeOfacStatus, type OfacClassificationRecord, type OfacStatus } from './ofacStatus';
import {
  markPass,
  markPending,
  upsertSection,
  type SectionClearance,
  type VerifyClearanceMap,
} from './verifyClearance';

/**
 * One OFAC SDN-list match row as returned by JHA OFACSrch.
 * Wire shape verified 2026-05-09 — see
 * `reference_jha_ofacsrch_wire_shape_20260509.md`.
 */
export interface OfacMatch {
  /** OFACMatchType — JHA enum (OFACAlias / OFACPrimary / OFACAddress / etc.). */
  matchType: string;
  /** AliasName — the matched name from the SDN list (the actual person/entity flagged). */
  aliasName: string;
  /** AliasId — numeric ID into the SDN catalog (for follow-up adjudication lookup). */
  aliasId: string;
  /** OFACMatchRef — JHA's record reference for this match. */
  matchRef: string;
}

export function extractMatches(resp: unknown): OfacMatch[] {
  const arr = (resp as { OFACSrchArray?: unknown }).OFACSrchArray;
  if (!Array.isArray(arr)) return [];
  const out: OfacMatch[] = [];
  for (const r of arr) {
    if (!r || typeof r !== 'object') continue;
    const row = r as Record<string, unknown>;
    out.push({
      matchType: typeof row.OFACMatchType === 'string' ? row.OFACMatchType : '',
      aliasName: typeof row.AliasName === 'string' ? row.AliasName : '',
      aliasId: typeof row.AliasId === 'string' ? row.AliasId : String(row.AliasId ?? ''),
      matchRef: typeof row.OFACMatchRef === 'string' ? row.OFACMatchRef : '',
    });
  }
  return out;
}

/**
 * Roll up JHA hits + classifier results into the canonical 3-state terminal
 * status. `unknown` from the classifier maps to current (fail safe).
 * 'archived-only' requires EVERY hit to be archived-only.
 */
export function rollUpStatus(
  hitCount: number,
  classifications: ReadonlyArray<OfacClassification>,
): OfacStatus {
  if (hitCount === 0) return 'clear';
  // No classifier rows came back — treat conservatively as current hit.
  if (classifications.length === 0) return 'hit-current';
  const hasCurrentSdnHit = classifications.some(
    (c) => c.classification === 'current' || c.classification === 'unknown',
  );
  if (hasCurrentSdnHit) return 'hit-current';
  const hasArchivedOnlyHit = classifications.some(
    (c) => c.classification === 'archived-only',
  );
  if (hasArchivedOnlyHit) return 'archived-only';
  // All hits classified but none flagged either way — fail-safe to current.
  return 'hit-current';
}

/** Screening name — mirrors the server's BuildScreenName (first+middle+last, else fullName). */
export function buildOfacScreenName(person: RaoPerson): string {
  const parts = [person.firstName, person.middleName, person.lastName]
    .filter((s) => s && (s as string).trim());
  if (parts.length > 0) return parts.join(' ').trim();
  if (person.fullName && (person.fullName as string).trim()) return (person.fullName as string).trim();
  return '';
}

/**
 * What the application keeps of a screening on the bank's own lists
 * (extra.ofacScreening). Same shape the server re-screen stamps
 * (ComplianceGateService.ScreeningStamp); the full record is
 * dbo.SanctionsScreenings by screeningId.
 */
export interface OfacScreeningStamp {
  screeningId: string;
  verdict: string;
  authority: string;
  topScore: number;
  summary: string;
  matcherVersion: string;
  candidates: Array<{
    sourceId: string;
    sourceEntryId: string;
    primaryName: string;
    matchedName: string;
    score: number;
    band: string;
  }>;
  forecast: Array<{ aliasId: string; entityId: string | null; assessment: string }>;
}

export interface OfacScanOutcome {
  status: OfacStatus;
  /** Core Director's OFACSrch hits (the forecast of its nightly report). */
  matches: OfacMatch[];
  records: OfacClassificationRecord[];
  /** The bank's own screening — present on every scan since 2026-10-19. */
  screening?: OfacScreeningStamp;
}

export function screeningStamp(r: SanctionsScreenResult): OfacScreeningStamp {
  return {
    screeningId: r.screeningId,
    verdict: r.verdict,
    authority: r.authority,
    topScore: r.topScore,
    summary: r.summary,
    matcherVersion: r.matcherVersion,
    candidates: r.candidates.slice(0, 5).map((c) => ({
      sourceId: c.sourceId,
      sourceEntryId: c.sourceEntryId,
      primaryName: c.primaryName,
      matchedName: c.matchedName,
      score: c.score,
      band: c.band,
    })),
    forecast: r.forecast.map((f) => ({ aliasId: f.aliasId, entityId: f.entityId, assessment: f.assessment })),
  };
}

export interface OfacScanContext {
  birthDate?: string;
  cartId?: string;
  personKey?: string;
}

/**
 * One screening on the bank's own government lists (Bill 2026-10-19: "Our
 * OFAC sourcing should be the golden rule"). The server searches every list
 * Risk has switched on and runs Core Director's OFACSrch as the forecast of
 * its nightly report; its hits come back labelled and are kept on the person
 * exactly as before (ofacMatches / ofacClassifications). Throws when the
 * screening call fails — callers persist 'pending' (never 'clear').
 */
export async function performOfacScan(
  client: ApiClient,
  fullName: string,
  comNameType: 'Indv' | 'Org',
  context: OfacScanContext = {},
): Promise<OfacScanOutcome> {
  const resp = await client.retail.ofac.screen({
    name: fullName,
    nameType: comNameType,
    ...(context.birthDate ? { birthDate: context.birthDate } : {}),
    ...(context.cartId ? { cartId: context.cartId } : {}),
    ...(context.personKey ? { personKey: context.personKey } : {}),
  });
  const r = resp.result;
  const matches: OfacMatch[] = r.coreMatches.map((m) => ({
    matchType: m.matchType ?? '',
    aliasName: m.aliasName ?? '',
    aliasId: m.aliasId ?? '',
    matchRef: m.matchRef ?? '',
  }));
  const records: OfacClassificationRecord[] = r.coreClassifications.map((c) => ({
    aliasId: c.aliasId,
    classification: c.classification,
    isCurrentSdn: c.isCurrentSdn,
    entityId: c.entityId,
    primaryName: c.primaryName,
    programs: [...(c.programs ?? [])],
    akaName: c.akaName,
    akaType: c.akaType,
  }));
  return { status: normalizeOfacStatus(r.status), matches, records, screening: screeningStamp(r) };
}

/**
 * Build the next `person.extra` for a scan result. Exactly the persistence
 * shape OfacScanSection has always written: ofacStatus + ofacScannedAt (+
 * matches/classifications when present), the `verifyClearance.ofac` mirror,
 * and invalidation of any prior FSR override (it applied to the prior
 * hit-set; a fresh scan requires a fresh override).
 */
export function buildOfacExtraPatch(
  extra: Record<string, unknown> | undefined,
  status: OfacStatus,
  matches: OfacMatch[],
  records: OfacClassificationRecord[],
  screening?: OfacScreeningStamp,
): Record<string, unknown> {
  const prev = extra ?? {};
  // ofacScannedAt (WT-BACKLOG B2) — the per-person scan timestamp the
  // server-side stale-rescreen keys off (risk.ofac.rescreenStaleAfterDays).
  // Stamped on EVERY scan write (client + server rescreen) — one age signal.
  const nextExtra: Record<string, unknown> = {
    ...prev,
    ofacStatus: status,
    ofacScannedAt: new Date().toISOString(),
  };
  if (matches.length > 0) {
    nextExtra.ofacMatches = matches;
  } else {
    delete nextExtra.ofacMatches;
  }
  if (records.length > 0) {
    nextExtra.ofacClassifications = records;
  } else {
    delete nextExtra.ofacClassifications;
  }
  if (screening) {
    nextExtra.ofacScreening = screening;
  } else {
    delete nextExtra.ofacScreening;
  }
  // Bill 2026-05-21 §7.4.2 — mirror into the shared verifyClearance.ofac row.
  const prevMap = prev['verifyClearance'] as VerifyClearanceMap | undefined;
  const nextClearance: SectionClearance | null =
    status === 'clear' || status === 'archived-only' ? markPass('ofac')
    : status === 'pending' ? markPending('ofac')
    : status === 'hit-current' ? { section: 'ofac', state: 'fail', clearedAt: new Date().toISOString() }
    : null;
  if (nextClearance) {
    nextExtra['verifyClearance'] = upsertSection(prevMap, nextClearance);
  }
  delete nextExtra['ofacOverride'];
  return nextExtra;
}

/** Parse `risk.ofac.rescreenStaleAfterDays` from config.systemSettings — server default 1 (Bill 2026-07-18). */
export function readOfacStaleDays(systemSettings: Record<string, unknown> | undefined): number {
  const raw = systemSettings?.['risk.ofac.rescreenStaleAfterDays'];
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n >= 0 ? n : 1;
}

/**
 * True when the person carries ANY recorded scan (`extra.ofacScannedAt`)
 * younger than `staleDays` days. `staleDays === 0` → nothing is ever fresh
 * (matches the server's `age >= staleDays` stale test).
 */
export function hasFreshOfacScan(person: RaoPerson, staleDays: number): boolean {
  if (staleDays <= 0) return false;
  const extra = (person as unknown as { extra?: Record<string, unknown> }).extra;
  const scannedAtRaw = extra?.['ofacScannedAt'];
  if (typeof scannedAtRaw !== 'string') return false;
  const scannedAt = Date.parse(scannedAtRaw);
  if (!Number.isFinite(scannedAt)) return false;
  const ageDays = (Date.now() - scannedAt) / 86_400_000;
  return ageDays < staleDays;
}

/**
 * True when the person already carries a SATISFACTORY screen fresh enough to
 * skip the auto-fire: status clear/archived-only (or an FSR override on the
 * verifyClearance.ofac row) scanned within `staleDays` days. A recorded HIT
 * is never satisfactory.
 */
export function hasFreshSatisfactoryOfac(person: RaoPerson, staleDays: number): boolean {
  const extra = (person as unknown as { extra?: Record<string, unknown> }).extra;
  if (!extra) return false;
  const clearance = (extra['verifyClearance'] as VerifyClearanceMap | undefined)?.ofac;
  const overridden = clearance?.state === 'fsr-override';
  const status = normalizeOfacStatus(extra['ofacStatus'] as string | undefined);
  const satisfactory = overridden || status === 'clear' || status === 'archived-only';
  return satisfactory && hasFreshOfacScan(person, staleDays);
}
